import { useRef, useState, type RefObject } from 'react';
import { endOfMonth, format, parseISO, subMonths } from 'date-fns';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LwTable, LW_DS_CLASS, useDialogEscapeFallback } from '@/components/lw';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  BONUS_AWARD_LABELS, saveProfitabilityBonus, voidProfitabilityBonus,
  type BonusInput, type ProfitabilityBonus
} from '@/services/loProfitabilityBonusService';
import { bonusPeriodContribution, type ReportUser } from './myCommissionReportModel';
import type { ReportPeriod } from './date-range/reportPeriod';
import { BonusEmailConfirmation } from './BonusEmailConfirmation';

const money = (value: number) => value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }).replace('-', '−');
const monthLabel = (value: string) => format(parseISO(value), 'MMMM yyyy');

export interface BonusTableProps {
  bonuses: ProfitabilityBonus[];
  period: ReportPeriod;
  users?: ReportUser[];
  showRecipient?: boolean;
  showHistory?: boolean;
  allowEmail?: boolean;
  onEdit?: (bonus: ProfitabilityBonus, trigger: HTMLButtonElement) => void;
  onVoid?: (bonus: ProfitabilityBonus, trigger: HTMLButtonElement) => void;
}

function BonusActions({ bonus, onEdit, onVoid, onHistory, onEmail }: Pick<BonusTableProps, 'onEdit' | 'onVoid'> & {
  bonus: ProfitabilityBonus;
  onHistory?: (trigger: HTMLButtonElement) => void;
  onEmail?: (trigger: HTMLButtonElement) => void;
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  const openingDialog = useRef(false);
  const open = (action: (element: HTMLButtonElement) => void) => {
    if (trigger.current) {
      openingDialog.current = true;
      action(trigger.current);
    }
  };
  return <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button ref={trigger} variant="ghost" size="sm" className="mc-bonus-action p-0"
        aria-label={`Bonus actions: ${bonus.recipient_name ?? 'Loan officer'}, ${monthLabel(bonus.award_month)}, ${bonus.award_type === 'units' ? 'Units' : 'Dollar Volume'}`}>
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className={`${LW_DS_CLASS} my-commissions mc-menu`} align="end" onCloseAutoFocus={event => {
      if (openingDialog.current) {
        event.preventDefault();
        openingDialog.current = false;
      }
    }}>
      {bonus.status === 'active' && onEdit && <DropdownMenuItem onSelect={() => open(element => onEdit(bonus, element))}>Edit bonus</DropdownMenuItem>}
      {bonus.status === 'active' && bonus.is_effective && onEmail && <DropdownMenuItem onSelect={() => open(onEmail)}>Congratulations email</DropdownMenuItem>}
      {onHistory && <DropdownMenuItem onSelect={() => open(onHistory)}>Bonus history</DropdownMenuItem>}
      {bonus.status === 'active' && onVoid && <DropdownMenuItem className="text-destructive" onSelect={() => open(element => onVoid(bonus, element))}>Void bonus</DropdownMenuItem>}
    </DropdownMenuContent>
  </DropdownMenu>;
}

export function ProfitabilityBonuses({ bonuses, period, users = [], showRecipient = true, showHistory = false, allowEmail = false, onEdit, onVoid }: BonusTableProps) {
  const [history, setHistory] = useState<{ id: string } | null>(null);
  const [email, setEmail] = useState<{ bonus: ProfitabilityBonus; trigger: HTMLButtonElement } | null>(null);
  const region = useRef<HTMLParagraphElement>(null);
  const historyTrigger = useRef<HTMLButtonElement | null>(null);
  const historyContent = useRef<HTMLDivElement>(null);
  const handleHistoryOpenChange = (open: boolean) => {
    if (!open) {
      setHistory(null);
    }
  };
  useDialogEscapeFallback(historyContent, () => handleHistoryOpenChange(false));
  const historyBonus = showHistory ? bonuses.find(bonus => bonus.id === history?.id) : undefined;
  const hasActions = Boolean(onEdit || onVoid || (allowEmail && bonuses.some(bonus => bonus.status === 'active' && bonus.is_effective)) || (showHistory && bonuses.some(bonus => bonus.history)));
  const columns = 5 + Number(showRecipient) + Number(hasActions);
  const sorted = [...bonuses].sort((a, b) => b.award_month.localeCompare(a.award_month)
    || (a.recipient_name ?? '').localeCompare(b.recipient_name ?? '') || a.award_type.localeCompare(b.award_type) || a.id.localeCompare(b.id));
  const contribution = bonuses.reduce((sum, bonus) => sum + bonusPeriodContribution(bonus, period), 0);
  const snapshotValue = (key: 'recipient_id' | 'award_type' | 'award_month' | 'amount' | 'status', state: Partial<ProfitabilityBonus>) => {
    const value = state[key];
    if (value === undefined) {
      return 'Not recorded';
    }
    if (key === 'amount') {
      return money(Number(value));
    }
    if (key === 'award_month') {
      return monthLabel(String(value));
    }
    if (key === 'award_type') {
      return BONUS_AWARD_LABELS[value as ProfitabilityBonus['award_type']];
    }
    if (key === 'recipient_id') {
      return users.find(user => user.user_id === value)?.name
        ?? (historyBonus?.recipient_id === value ? historyBonus.recipient_name : null) ?? `Unlisted officer (${value})`;
    }
    return value === 'voided' ? 'Voided' : 'Active';
  };
  return <section className="min-w-0 mc-bonuses" aria-label="Profitability bonuses">
    <p className="mc-bonus-scroll-hint mb-2 text-xs text-muted-foreground sm:hidden">Scroll horizontally to see all columns.</p>
    <div className="overflow-hidden rounded-md border bg-card">
      <LwTable presentation="canonical" density="compact" ariaLabel="Profitability bonuses" scrollRegionLabel="Bonus table">
        <TableHeader><TableRow>
          {showRecipient && <TableHead className="min-w-40">Loan officer</TableHead>}
          <TableHead className="min-w-32">Winning month</TableHead>
          <TableHead className="min-w-36">Award</TableHead>
          <TableHead className="min-w-28 text-right">Amount</TableHead>
          <TableHead className="min-w-32 text-right">Included in period</TableHead>
          <TableHead className="min-w-32">Status</TableHead>
          {hasActions && <TableHead className="mc-bonus-actions-heading w-12"><span className="sr-only">Actions</span></TableHead>}
        </TableRow></TableHeader>
        <TableBody>
          {sorted.length === 0 && <TableRow><TableCell colSpan={columns} className="py-8 text-center text-muted-foreground">No bonuses in this period.</TableCell></TableRow>}
          {sorted.map(bonus => <TableRow key={bonus.id}>
            {showRecipient && <TableCell data-label="Loan officer" className="font-medium">{bonus.recipient_name ?? 'Loan officer'}</TableCell>}
            <TableCell data-label="Winning month">{monthLabel(bonus.award_month)}</TableCell>
            <TableCell data-label="Award">{bonus.award_type === 'units' ? 'Units' : 'Dollar Volume'}</TableCell>
            <TableCell data-label="Amount" className="text-right tabular-nums">{money(bonus.amount)}</TableCell>
            <TableCell data-label="Included in period" className="text-right font-medium tabular-nums">{money(bonusPeriodContribution(bonus, period))}</TableCell>
            <TableCell data-label="Status"><span className={bonus.status === 'voided' ? 'text-muted-foreground' : bonus.is_effective ? 'text-success' : 'text-warning'}>
              {bonus.status === 'voided' ? 'Voided' : bonus.is_effective ? 'Active' : 'Scheduled'}
            </span>{bonus.status === 'active' && !bonus.is_effective && <div className="text-xs text-muted-foreground">Effective {format(parseISO(bonus.effective_date), 'MMM d, yyyy')} PT</div>}</TableCell>
            {hasActions && <TableCell className="mc-bonus-actions text-right">
              {((bonus.status === 'active' && (onEdit || onVoid || (allowEmail && bonus.is_effective))) || (showHistory && bonus.history)) && <BonusActions bonus={bonus} onEdit={onEdit} onVoid={onVoid}
                onEmail={allowEmail ? trigger => setEmail({ bonus, trigger }) : undefined}
                onHistory={showHistory && bonus.history ? trigger => {
                  historyTrigger.current = trigger;
                  setHistory({ id: bonus.id });
                } : undefined} />}
            </TableCell>}
          </TableRow>)}
        </TableBody>
        {bonuses.length > 0 && <TableFooter><TableRow>
          <TableCell colSpan={showRecipient ? 4 : 3}>Total included in LO Profitability</TableCell>
          <TableCell className="text-right tabular-nums">{money(contribution)}</TableCell>
          <TableCell colSpan={hasActions ? 2 : 1} />
        </TableRow></TableFooter>}
      </LwTable>
    </div>
    <p ref={region} tabIndex={-1} className="mt-2 text-xs text-muted-foreground">LO of the Month awards are profitability credits, not cash payments or earned commissions.</p>
    {email && <ProfitabilityBonusEmailDialog bonus={email.bonus} trigger={email.trigger} fallbackFocusRef={region} onClose={() => setEmail(null)} />}
    <Dialog open={Boolean(historyBonus)} onOpenChange={handleHistoryOpenChange}>
      <DialogContent ref={historyContent} className={`${LW_DS_CLASS} my-commissions mc-modal max-h-[90dvh] overflow-y-auto`} overlayClassName={`${LW_DS_CLASS} my-commissions mc-modal-overlay`} data-lw-overlay-layer="" onCloseAutoFocus={event => {
        event.preventDefault();
        if (historyTrigger.current?.isConnected) {
          historyTrigger.current.focus();
        } else {
          region.current?.focus();
        }
      }}>
        <DialogHeader><DialogTitle>Bonus history</DialogTitle><DialogDescription>
          {historyBonus?.recipient_name} · {historyBonus && monthLabel(historyBonus.award_month)} · {historyBonus && BONUS_AWARD_LABELS[historyBonus.award_type]}
        </DialogDescription></DialogHeader>
        <ol className="space-y-4 text-sm">
          {historyBonus?.history?.map(event => <li key={event.id} className="space-y-1 border-b pb-3 last:border-0 break-words">
            <p className="font-medium">{event.event_type === 'created' ? 'Created' : event.event_type === 'updated' ? 'Updated' : 'Voided'} by {event.actor_name ?? 'Recorded actor'}</p>
            <p className="text-xs text-muted-foreground">{new Date(event.created_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} PT · Version {event.version}</p>
            {(['recipient_id', 'award_type', 'award_month', 'amount', 'status'] as const).map(key => {
              if (event.after_state[key] === undefined || (event.before_state && event.before_state[key] === event.after_state[key])) {
                return null;
              }
              const label = { recipient_id: 'Loan officer', award_type: 'Award', award_month: 'Winning month', amount: 'Amount', status: 'Status' }[key];
              return <p key={key}>{label}: {event.before_state && <>{snapshotValue(key, event.before_state)} → </>}{snapshotValue(key, event.after_state)}</p>;
            })}
            {event.reason && <p>Reason: {event.reason}</p>}
          </li>)}
          {historyBonus?.history?.length === 0 && <li>No history entries returned.</li>}
        </ol>
      </DialogContent>
    </Dialog>
  </section>;
}

export interface BonusEditorState {
  bonus?: ProfitabilityBonus;
  voiding?: boolean;
  trigger: HTMLButtonElement;
}

/** Keep the reviewed ID/version fixed while previewing or retrying the same email. */
export function ProfitabilityBonusEmailDialog({ bonus, trigger, fallbackFocusRef, onClose }: {
  bonus: ProfitabilityBonus;
  trigger: HTMLButtonElement;
  fallbackFocusRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}) {
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const content = useRef<HTMLDivElement>(null);
  const handleOpenChange = (open: boolean) => {
    if (!open && !inFlight.current) {
      onClose();
    }
  };
  useDialogEscapeFallback(content, () => handleOpenChange(false));
  return <Dialog open onOpenChange={handleOpenChange}>
    <DialogContent ref={content} className={`${LW_DS_CLASS} my-commissions mc-modal max-h-[90dvh] overflow-y-auto`} overlayClassName={`${LW_DS_CLASS} my-commissions mc-modal-overlay`} data-lw-overlay-layer="" hideCloseButton={busy} onCloseAutoFocus={event => {
      event.preventDefault();
      if (trigger.isConnected && !trigger.disabled) {
        trigger.focus();
      } else {
        fallbackFocusRef.current?.focus();
      }
    }}>
      <BonusEmailConfirmation bonus={bonus} context="rowMenu" onClose={onClose} onBusyChange={value => {
        inFlight.current = value;
        setBusy(value);
      }} />
    </DialogContent>
  </Dialog>;
}

export function AddProfitabilityBonusButton({ disabled, onOpen }: {
  disabled: boolean;
  onOpen: (state: BonusEditorState) => void;
}) {
  return <Button size="sm" data-preserve-disabled disabled={disabled} onClick={event => onOpen({ trigger: event.currentTarget })}>
    <Plus className="mr-1 h-4 w-4" />Add Bonus
  </Button>;
}

/** Mount once per opening; an uncertain request retains its UUID and original payload. */
export function ProfitabilityBonusDialog({ editor, users, selectedUserId, currentMonth, period, fallbackFocusRef, onClose, onSaved }: {
  editor: BonusEditorState;
  users: ReportUser[];
  selectedUserId: string | null;
  currentMonth: string;
  period: ReportPeriod;
  fallbackFocusRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { bonus, voiding } = editor;
  const [input, setInput] = useState<BonusInput>(() => ({
    id: bonus?.id ?? crypto.randomUUID(),
    recipientId: bonus?.recipient_id ?? (users.find(user => user.user_id === selectedUserId && user.bonus_eligible)?.user_id ?? ''),
    awardType: bonus?.award_type ?? 'dollar_volume',
    awardMonth: bonus?.award_month ?? format(subMonths(parseISO(currentMonth), 1), 'yyyy-MM-01'),
    amount: bonus?.amount.toFixed(2) ?? '',
    expectedVersion: bonus?.version ?? null,
    reason: ''
  }));
  const [saving, setSaving] = useState(false);
  const [savedBonus, setSavedBonus] = useState<ProfitabilityBonus | null>(null);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const request = useRef<BonusInput | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const content = useRef<HTMLDivElement>(null);
  const handleOpenChange = (open: boolean) => {
    if (!open && !inFlight.current) {
      onClose();
    }
  };
  useDialogEscapeFallback(content, () => handleOpenChange(false));
  const eligible = users.filter(user => user.bonus_eligible || user.user_id === bonus?.recipient_id);
  const update = <K extends keyof BonusInput>(key: K, value: BonusInput[K]) => setInput(previous => ({ ...previous, [key]: value }));
  const title = voiding ? 'Void profitability bonus' : bonus ? 'Edit profitability bonus' : 'Add profitability bonus';
  const submit = async () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setError('');
    const payload = request.current ?? input;
    try {
      const saved = voiding
        ? await voidProfitabilityBonus(payload.id, payload.expectedVersion ?? 0, payload.reason)
        : await saveProfitabilityBonus(payload, currentMonth);
      const name = users.find(user => user.user_id === saved.recipient_id)?.name ?? 'Loan officer';
      // Mutation RPCs return the stored row, not the report's derived date fields.
      const effectiveDate = format(endOfMonth(parseISO(saved.award_month)), 'yyyy-MM-dd');
      const outsidePeriod = effectiveDate < format(period.from, 'yyyy-MM-dd') || effectiveDate > format(period.to, 'yyyy-MM-dd');
      onSaved(`${name}: ${monthLabel(saved.award_month)} bonus ${saved.status === 'voided' ? 'voided' : 'saved'} (${money(saved.amount)}). ${outsidePeriod ? 'Outside the selected report period. ' : ''}Your report filters are unchanged.`);
      if (!bonus && saved.status === 'active') {
        setSavedBonus({ ...saved, recipient_name: name });
      } else {
        onClose();
      }
    } catch (caught) {
      const failure = caught as { message?: string; code?: string };
      // Postgres errors are definitive. Network failures might have committed:
      // freeze the payload and retry the same operation, never silently create anew.
      const networkUncertain = !failure.code && /fetch|network|timeout|connection/i.test(failure.message ?? '');
      if (networkUncertain) {
        request.current = { ...payload };
        setUncertain(true);
      }
      setError(failure.code === '23505'
        ? 'An active bonus already exists for this loan officer, category and month. Edit that bonus instead.'
        : `${failure.message ?? 'Could not save bonus.'}${networkUncertain ? ' The result is uncertain; retry this same request or close and refresh to review.' : ''}`);
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };
  return <Dialog open onOpenChange={handleOpenChange}>
    <DialogContent ref={content} className={`${LW_DS_CLASS} my-commissions mc-modal max-h-[90dvh] overflow-y-auto`} overlayClassName={`${LW_DS_CLASS} my-commissions mc-modal-overlay`} data-lw-overlay-layer="" hideCloseButton={saving}
      onCloseAutoFocus={event => {
        event.preventDefault();
        if (editor.trigger.isConnected && !editor.trigger.disabled) {
          editor.trigger.focus();
        } else {
          fallbackFocusRef.current?.focus();
        }
      }}>
      {savedBonus ? <BonusEmailConfirmation bonus={savedBonus} context="postCreate" currentMonth={currentMonth} onClose={onClose} onBusyChange={busy => {
        inFlight.current = busy;
        setSaving(busy);
      }} /> : <><DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>This changes LO Profitability and the available recapture balance. It does not create a payroll payment.</DialogDescription>
      </DialogHeader>
      <form className="min-w-0 space-y-4" onSubmit={event => {
        event.preventDefault();
        void submit();
      }}>
        <fieldset disabled={saving || uncertain} className="min-w-0 space-y-4">
          {!voiding && <>
            <div className="space-y-1">
              <Label htmlFor="bonus-recipient">Loan officer</Label>
              <Select value={input.recipientId} onValueChange={value => update('recipientId', value)} disabled={saving || uncertain}>
                <SelectTrigger id="bonus-recipient"><SelectValue placeholder="Choose loan officer" /></SelectTrigger>
                <SelectContent className={`${LW_DS_CLASS} my-commissions mc-menu`}>{eligible.map(user => <SelectItem key={user.user_id} value={user.user_id}>{user.name ?? user.email ?? user.user_id}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bonus-category">Award category</Label>
              <Select value={input.awardType} onValueChange={value => update('awardType', value as BonusInput['awardType'])} disabled={saving || uncertain}>
                <SelectTrigger id="bonus-category"><SelectValue /></SelectTrigger>
                <SelectContent className={`${LW_DS_CLASS} my-commissions mc-menu`}><SelectItem value="dollar_volume">Dollar Volume</SelectItem><SelectItem value="units">Units</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bonus-month">Winning month</Label>
              <Input id="bonus-month" type="month" min="2026-07" max={currentMonth.slice(0, 7)} value={input.awardMonth.slice(0, 7)} required
                onChange={event => update('awardMonth', `${event.target.value}-01`)} />
              {/^\d{4}-(0[1-9]|1[0-2])-01$/.test(input.awardMonth) && <p className="text-xs text-muted-foreground">
                Effective {format(endOfMonth(parseISO(input.awardMonth)), 'MMM d, yyyy')} (Pacific). {input.awardMonth === currentMonth && 'Scheduled until that day.'}
              </p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="bonus-amount">Amount ($)</Label>
              <Input id="bonus-amount" inputMode="decimal" value={input.amount} required onChange={event => update('amount', event.target.value)} />
            </div>
          </>}
          {bonus && <>
            {voiding && <p className="text-sm font-medium">{bonus.recipient_name ?? 'Loan officer'} · {monthLabel(bonus.award_month)} · {BONUS_AWARD_LABELS[bonus.award_type]} · {money(bonus.amount)}</p>}
            <p className="text-xs text-warning">Reducing or voiding a bonus can leave a negative available balance if recapture already used it. Existing recaptures and payments will not be reversed.</p>
            <div className="space-y-1">
              <Label htmlFor="bonus-reason">{voiding ? 'Void reason' : 'Correction reason'} (internal)</Label>
              <Textarea id="bonus-reason" value={input.reason} required onChange={event => update('reason', event.target.value)} />
            </div>
          </>}
        </fieldset>
        {error && <p role="alert" className="break-words text-sm text-destructive">{error}</p>}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" disabled={saving} onClick={onClose}>Cancel</Button>
          <Button type="submit" variant={voiding ? 'destructive' : 'default'} disabled={saving}>
            {saving ? 'Saving…' : uncertain ? 'Retry same request' : voiding ? 'Void bonus' : 'Save bonus'}
          </Button>
        </div>
      </form></>}
    </DialogContent>
  </Dialog>;
}
